import type { GraphNode } from '../../lib/graph/types';
import type { HierarchyConfig } from './DomainDataProfile';

export interface HierarchyMaps {
  childrenByParent: Map<string, string[]>;
  parentsByChild: Map<string, string[]>;
  rootIds: Set<string>;
}

function normalizeParentId(value: unknown): string | null {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : null;
  }
  return null;
}

export function buildHierarchyMaps<TProps extends Record<string, unknown>>(
  nodes: Array<GraphNode<string, TProps>>,
  config: HierarchyConfig
): HierarchyMaps {
  const childrenByParent = new Map<string, string[]>();
  const parentsByChild = new Map<string, string[]>();
  const rootIds = new Set<string>(nodes.map((node) => node.id));

  const registerEdge = (childId: string, parentId: string) => {
    const children = childrenByParent.get(parentId) ?? [];
    children.push(childId);
    childrenByParent.set(parentId, children);

    const parents = parentsByChild.get(childId) ?? [];
    parents.push(parentId);
    parentsByChild.set(childId, parents);

    rootIds.delete(childId);
  };

  if (config.type === 'flat') {
    nodes.forEach((node) => {
      if (!childrenByParent.has(node.id)) {
        childrenByParent.set(node.id, []);
      }
    });
    return { childrenByParent, parentsByChild, rootIds };
  }

  if (config.type === 'single-parent') {
    nodes.forEach((node) => {
      const parentId = normalizeParentId(node.props?.[config.parentProp]);
      if (parentId) {
        registerEdge(node.id, parentId);
      }
    });
  }

  if (config.type === 'multi-parent') {
    nodes.forEach((node) => {
      config.parentProps.forEach((prop) => {
        const parentId = normalizeParentId(node.props?.[prop]);
        if (parentId) {
          registerEdge(node.id, parentId);
        }
      });
    });
  }

  nodes.forEach((node) => {
    if (!childrenByParent.has(node.id)) {
      childrenByParent.set(node.id, []);
    }
  });

  return { childrenByParent, parentsByChild, rootIds };
}
